import { useEffect, useState } from 'react'

// A hash router in ~50 lines.
//
// Deliberately not a routing library: this app has a handful of screens and no
// nested layouts, data loaders or code splitting to justify one. Hash routing
// also means the Go server can serve the built index.html from a single path
// with no rewrite rules.

export type SettingsSection = 'brands' | 'avatars' | 'assets' | 'templates' | 'workspace' | 'keys'
export type RegenerationMode = 'scenes'

type PageRoute =
  | { name: 'jobs' }
  | { name: 'job'; id: string }
  | { name: 'new-job'; sourceID?: string; regenerationMode?: RegenerationMode }
  | { name: 'getting-started' }
  | { name: 'new-workspace' }
  // `sub: 'new'` is the create form for a section, e.g. #/settings/avatars/new.
  // A form that is only a boolean in a component cannot be linked to, cannot be
  // left with the back button, and does not survive a reload -- and a reload
  // landing somewhere other than where you were is the part people notice.
  | { name: 'settings'; section: SettingsSection; sub?: 'new' }

export type Route = PageRoute & { workspace?: string }

const SECTIONS: SettingsSection[] = ['brands', 'avatars', 'assets', 'templates', 'workspace', 'keys']

const isSection = (s: string): s is SettingsSection =>
  (SECTIONS as string[]).includes(s)

function parseParts(parts: string[]): PageRoute {
  const [head, second, third, fourth] = parts
  const sub = third === 'new' ? ('new' as const) : undefined

  switch (head) {
    case 'videos':
      if (second === 'new') {
        return {
          name: 'new-job',
          sourceID: third === 'from' ? fourth : undefined,
          regenerationMode: third === 'from' ? 'scenes' : undefined,
        }
      }
      return second ? { name: 'job', id: second } : { name: 'jobs' }
    case 'new':
      return {
		name: 'new-job',
		sourceID: second === 'from' ? third : undefined,
		// Legacy /full links now open the same unified scene-selection flow.
		regenerationMode: second === 'from' ? 'scenes' : undefined,
	  }
    case 'getting-started':
      return { name: 'getting-started' }
    case 'workspaces':
      return second === 'new' ? { name: 'new-workspace' } : { name: 'jobs' }
    case 'job':
      return second ? { name: 'job', id: second } : { name: 'jobs' }
    case 'settings':
      return { name: 'settings', section: isSection(second) ? second : 'brands', sub }
    default:
      // Brands, avatars and templates used to be top-level. Old links resolve
      // to their new home rather than silently landing on the ads list.
      if (isSection(head)) {
        return { name: 'settings', section: head, sub: second === 'new' ? 'new' : undefined }
      }
      return { name: 'jobs' }
  }
}

function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean)
  if (parts[0] === 'w' && parts[1]) {
    return { ...parseParts(parts.slice(2)), workspace: decodeURIComponent(parts[1]) }
  }
  return parseParts(parts)
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parse(window.location.hash))
  useEffect(() => {
    const on = () => setRoute(parse(window.location.hash))
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])
  return route
}

let activeWorkspace = ''
export const setActiveWorkspaceRoute = (slug: string) => { activeWorkspace = slug }

export const href = (r: Route): string => {
  const workspace = r.workspace ?? activeWorkspace
  const prefix = workspace && r.name !== 'new-workspace'
    ? `#/w/${encodeURIComponent(workspace)}`
    : '#'
  switch (r.name) {
    case 'job': return `${prefix}/videos/${r.id}`
    case 'new-job': return r.sourceID
	  ? `${prefix}/videos/new/from/${r.sourceID}`
	  : `${prefix}/videos/new`
    case 'getting-started': return `${prefix}/getting-started`
    case 'new-workspace': return '#/workspaces/new'
    case 'settings': return `${prefix}/settings/${r.section}${r.sub ? `/${r.sub}` : ''}`
    default: return `${prefix}/videos`
  }
}

/** Asked before every navigation. Return false to stop it.
 *
 *  Exists for forms holding work that would be lost by leaving -- a screen that
 *  quietly discards a generation someone paid for is worse than one that asks.
 *  A guard that blocks is expected to show its own prompt and then navigate
 *  itself, so the decision stays where the context is. */
type NavGuard = (to: Route) => boolean

let guard: NavGuard | null = null

export const setNavGuard = (g: NavGuard | null) => { guard = g }

export const go = (r: Route) => {
  if (guard && !guard(r)) return
  window.location.hash = href(r)
}

/** Navigate past the guard, for the guard's own "yes, leave" path. */
export const goForce = (r: Route) => {
  guard = null
  window.location.hash = href(r)
}
